import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { toast } from 'sonner';
import { ticketsApi, usersApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { 
  Ticket as TicketIcon, 
  MagnifyingGlass,
  X,
  User,
  Star
} from '@phosphor-icons/react';
import { formatRelativeTime } from '@/lib/utils';

const isTicketBreached = (ticket) => {
  if (!ticket.sla_deadline) return false;
  if (['completed', 'closed', 'rejected'].includes(ticket.status)) return false;
  return new Date(ticket.sla_deadline) < new Date();
};

export const AdminTickets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantConfig = useAppStore((state) => state.tenantConfig);

  // Staff filter from URL (set by User Directory "View Tickets" button)
  const staffId   = searchParams.get('staff_id') || '';
  const staffName = searchParams.get('staff_name') || '';
  const staffRole = searchParams.get('staff_role') || '';

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedSla, setSelectedSla] = useState('all');
  const [selectedStaffRole, setSelectedStaffRole] = useState('all');
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    if (staffRole) {
      setSelectedStaffRole(staffRole);
    }
  }, [staffRole]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data } = await ticketsApi.list({ page_size: 100 });
      setTickets(data.data || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      const { data } = await usersApi.list();
      const staff = (data || []).filter(u => u.role !== 'customer');
      staff.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setStaffList(staff);
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStaffList();
  }, []);

  const clearStaffFilter = () => {
    setSearchParams({});
  };

  const handleStaffChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('staff_id');
      newParams.delete('staff_name');
      newParams.delete('staff_role');
    } else {
      const staff = staffList.find(s => s.id === value);
      if (staff) {
        newParams.set('staff_id', staff.id);
        newParams.set('staff_name', staff.full_name);
        newParams.set('staff_role', staff.role);
      }
    }
    setSearchParams(newParams);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Admin';
      case 'foc': return 'Field Ops';
      case 'agent': return 'Agent';
      case 'technician': return 'Tech';
      default: return role;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    // Staff filter (agent or technician)
    if (staffId) {
      const matchesStaff = ticket.agent_id === staffId || ticket.tech_id === staffId;
      if (!matchesStaff) return false;
    }

    // Staff role category filter
    if (selectedStaffRole !== 'all') {
      if (selectedStaffRole === 'agent' && !ticket.agent_id) return false;
      if (selectedStaffRole === 'technician' && !ticket.tech_id) return false;
      if (selectedStaffRole === 'foc' && !ticket.foc_id) return false;
    }

    const matchesSearch = ticket.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.agent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.tech_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.foc_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
    if (!matchesSearch) return false;

    if (selectedStatus === 'open') {
      if (['completed', 'closed', 'rejected'].includes(ticket.status)) return false;
    } else if (selectedStatus !== 'all') {
      if (ticket.status !== selectedStatus) return false;
    }

    if (selectedPriority !== 'all') {
      if (ticket.priority !== selectedPriority) return false;
    }

    if (selectedSla === 'breached') {
      return isTicketBreached(ticket);
    }
    if (selectedSla === 'within') {
      return ticket.sla_deadline && !isTicketBreached(ticket);
    }

    return true;
  });

  const baseTickets = staffId 
    ? tickets.filter(t => t.agent_id === staffId || t.tech_id === staffId) 
    : tickets;

  const statCounts = {
    total: baseTickets.length,
    open: baseTickets.filter(t => !['completed', 'closed', 'rejected'].includes(t.status)).length,
    urgent: baseTickets.filter(t => t.priority === 'urgent').length,
    completed: baseTickets.filter(t => t.status === 'completed' || t.status === 'closed').length,
  };

  const ratedCount = staffId ? baseTickets.filter(t => t.is_rated).length : 0;

  return (
    <DashboardLayout role="super_admin">
      <div className="page-padding max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-black tracking-tight mb-2">Tickets</h1>
          <p className="text-muted-foreground">Manage all service requests across your organization</p>
        </div>

        {/* Staff filter banner */}
        {staffId && (
          <div className="mb-5 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-800">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <User size={16} />
              Filtered by {staffRole === 'technician' ? 'Technician' : 'Agent'}:{' '}
              <span className="font-black">{staffName}</span>
              <span className="ml-2 text-blue-600 text-xs font-normal">
                {statCounts.total} ticket{statCounts.total !== 1 ? 's' : ''} · {ratedCount} rated
              </span>
            </div>
            <button
              onClick={clearStaffFilter}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
            >
              <X size={14} /> Clear filter
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className={`p-4 border cursor-pointer hover:bg-slate-50 transition-all ${
              selectedStatus === 'all' && selectedPriority === 'all' && selectedSla === 'all' ? 'ring-2 ring-primary bg-slate-50/50' : ''
            }`}
            onClick={() => { setSelectedStatus('all'); setSelectedPriority('all'); setSelectedSla('all'); }}
          >
            <p className="label-micro mb-1">Total</p>
            <p className="text-3xl font-black">{statCounts.total}</p>
          </Card>
          <Card 
            className={`p-4 border cursor-pointer hover:bg-slate-50 transition-all ${
              selectedStatus === 'open' ? 'ring-2 ring-blue-500 bg-blue-50/20' : ''
            }`}
            onClick={() => { setSelectedStatus('open'); setSelectedPriority('all'); setSelectedSla('all'); }}
          >
            <p className="label-micro mb-1">Open</p>
            <p className="text-3xl font-black text-blue-600">{statCounts.open}</p>
          </Card>
          <Card 
            className={`p-4 border cursor-pointer hover:bg-slate-50 transition-all ${
              selectedPriority === 'urgent' ? 'ring-2 ring-red-500 bg-red-50/20' : ''
            }`}
            onClick={() => { setSelectedStatus('all'); setSelectedPriority('urgent'); setSelectedSla('all'); }}
          >
            <p className="label-micro mb-1">Urgent</p>
            <p className="text-3xl font-black text-red-500">{statCounts.urgent}</p>
          </Card>
          <Card 
            className={`p-4 border cursor-pointer hover:bg-slate-50 transition-all ${
              selectedStatus === 'completed' ? 'ring-2 ring-emerald-500 bg-emerald-50/20' : ''
            }`}
            onClick={() => { setSelectedStatus('completed'); setSelectedPriority('all'); setSelectedSla('all'); }}
          >
            <p className="label-micro mb-1">Resolved</p>
            <p className="text-3xl font-black text-emerald-600">{statCounts.completed}</p>
          </Card>
        </div>

        {/* Filters */}
        <div className="filter-scroll mb-6">
          <div className="relative min-w-[180px] flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[140px] shrink-0"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open (Active)</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="picked_up">Picked Up</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedPriority} onValueChange={setSelectedPriority}>
            <SelectTrigger className="w-[120px] shrink-0"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedSla} onValueChange={setSelectedSla}>
            <SelectTrigger className="w-[120px] shrink-0"><SelectValue placeholder="SLA Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SLA</SelectItem>
              <SelectItem value="breached">Breached</SelectItem>
              <SelectItem value="within">Within SLA</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedStaffRole} onValueChange={(val) => {
            setSelectedStaffRole(val);
            handleStaffChange('all');
          }}>
            <SelectTrigger className="w-[130px] shrink-0"><SelectValue placeholder="Staff Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="agent">Support Agents</SelectItem>
              <SelectItem value="technician">Technicians</SelectItem>
              <SelectItem value="foc">Field Ops</SelectItem>
            </SelectContent>
          </Select>
          <Select value={staffId || 'all'} onValueChange={handleStaffChange}>
            <SelectTrigger className="w-[160px] shrink-0"><SelectValue placeholder="Staff" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {staffList
                .filter(staff => selectedStaffRole === 'all' || staff.role === selectedStaffRole)
                .map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>
                    {staff.full_name} ({getRoleLabel(staff.role)})
                  </SelectItem>
                ))
              }
            </SelectContent>
          </Select>
        </div>

        {/* Tickets Table */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredTickets.length > 0 ? (
          <div className="space-y-2">
            {filteredTickets.map((ticket) => (
              <Card
                key={ticket.id}
                className="p-4 hover:bg-muted/30 transition-all cursor-pointer border"
                onClick={() => navigate(`/ops/admin/tickets/${ticket.id}`)}
              >
                {/* Mobile layout: stacked */}
                <div className="flex flex-col gap-2 sm:hidden">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-bold text-sm">{ticket.ticket_number}</p>
                        {ticket.agent_stars && <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 text-[9px] font-black">A:{ticket.agent_stars}★</span>}
                        {ticket.tech_stars && <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 text-[9px] font-black">T:{ticket.tech_stars}★</span>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{ticket.customer_name}</p>
                    </div>
                    <PriorityBadge priority={ticket.priority} />
                  </div>
                  <p className="text-sm font-medium truncate">{ticket.product_name}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={ticket.status} />
                    <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
                  </div>
                  <p className="text-xs text-muted-foreground">{ticket.agent_name || '—'}</p>
                </div>
                {/* Desktop layout: grid */}
                <div className="hidden sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center">
                  <div className="col-span-3 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-sm">{ticket.ticket_number}</p>
                      {ticket.is_rated && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {ticket.agent_stars && (
                            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 text-[9px] font-black" title={`Agent Rating: ${ticket.agent_stars}★`}>
                              A: {ticket.agent_stars}★
                            </span>
                          )}
                          {ticket.tech_stars && (
                            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 text-[9px] font-black" title={`Tech Rating: ${ticket.tech_stars}★`}>
                              T: {ticket.tech_stars}★
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{ticket.customer_name}</p>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-medium truncate">{ticket.product_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{ticket.issue_category}</p>
                  </div>
                  <div className="col-span-2">
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div className="col-span-1">
                    <PriorityBadge priority={ticket.priority} />
                  </div>
                  <div className="col-span-2">
                    <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="text-xs text-muted-foreground truncate block">{ticket.agent_name || '—'}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border">
            <TicketIcon size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground">
              {staffId ? `No tickets found for ${staffName}` : 'No tickets found'}
            </p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
